import { useContext, useEffect, useState } from 'react';
import {
  CheckboxChangeEvent,
  InputChangeEvent,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { GameDocumentContext } from '../../../contexts/game-document';
import RequiredFields from '../../../types/required-fields';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import { Typography } from '@progress/kendo-react-common';
import { ItemEditor } from '../../../features/game-document/items';
import cloneDeep from 'lodash.clonedeep';
import Toolbar from '../toolbar';
import {
  AddItemAsync,
  AddResourceAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import { uuid } from '../../../types/common-helper';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  ItemEntity,
  ResourceEntity,
  BankItemGroup
} from '../../../types/game-document/';
import { useBreadcrumb } from '../../../contexts/breadcrumb';

export const NewItem = () => {
  const titleGuid = uuid();
  const summaryGuid = uuid();
  const imageGuid = uuid();
  const titleResource: ResourceEntity = {
    id: titleGuid,
    name: 'Title',
    description: '',
    type: 'text',
    value: ''
  };

  const summaryResource: ResourceEntity = {
    id: summaryGuid,
    name: 'Summary',
    description: '',
    type: 'text',
    value: ''
  };

  const imageResource: ResourceEntity = {
    id: imageGuid,
    name: imageGuid,
    description: '',
    type: 'image',
    value: '',
    size: 0,
    category: 'Inventory images'
  };

  const [state, setState] = useContext(GameDocumentContext);
  const [entity, setEntity] = useState<ItemEntity>({
    titleResId: titleGuid,
    imageResId: imageGuid,
    description: '',
    name: '',
    hideInGame: false,
    summaryResId: summaryGuid,
    id: '',
    maxStock: undefined,
    useBank: false,
    bankItemGroupId: undefined
  });
  const [resources, setResources] = useState<ResourceEntity[]>([
    titleResource,
    summaryResource,
    imageResource
  ]);

  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<ItemEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);
  const navigate = useNavigate();

  const handleInputChange = (event: InputChangeEvent) => {
    const { name, value } = event.target;
    if (name === 'titleResId' || name === 'summaryResId') {
      const key = entity[name]!;
      setResources(
        resources.map((item) =>
          item.id === key ? { ...item, value: value! as string } : { ...item }
        )
      );
    } else {
      setEntity({ ...entity, [name!]: value });
    }
  };

  const handleTextAreaChange = (event: TextAreaChangeEvent) => {
    const { name, value } = event.target;
    setEntity({ ...entity, [name!]: value });
  };

  const handleImageSelected = (
    url: string,
    size: number,
    name?: string,
    category?: string
  ) => {
    setResources(
      resources.map((item) =>
        item.id === entity.imageResId
          ? {
              ...item,
              value: url,
              size: size,
              name: name || item.name,
              description: name ?? '',
              category: category
            }
          : { ...item }
      )
    );
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredFields.map((input) => {
      const entityValue = entity[input.name as keyof ItemEntity];

      if (input.name === 'titleResId') {
        const resourceIndex = resources.findIndex(
          (resource) => resource.id === entityValue
        );
        if (resources[resourceIndex]?.value === '') {
          input.errorMessage = ERR_INPUT_REQUIRED;
        } else {
          input.errorMessage = '';
        }
      }

      return input;
    });
    setRequiredFields(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  const handleHideItemChange = (event: CheckboxChangeEvent) => {
    const { name, value } = event.target;
    let updatedEntity = {
      ...entity,
      [name!]: value
    };

    if (name === 'useBank' && !value) {
      updatedEntity.maxStock = undefined;
      updatedEntity.bankItemGroupId = undefined;
    }

    setEntity(updatedEntity);
  };

  const onRemoveBankItemGroup = (index: number) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    groups.splice(index, 1);

    setState((prev) =>
      UpdateGameDocState(prev, {
        ...prev.gameDocument!,
        assets: {
          ...prev.gameDocument?.assets,
          bankItemGroups: groups
        }
      })
    );
  };

  const onAddBankItemGroup = (group: BankItemGroup) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    groups.push(group);

    setEntity((prev) => ({
      ...prev,
      bankItemGroupId: group.id
    }));

    setState((prev) =>
      UpdateGameDocState(prev, {
        ...prev.gameDocument!,
        assets: {
          ...prev.gameDocument?.assets,
          bankItemGroups: groups
        }
      })
    );
  };

  const onUpdateBankItemGroup = (index: number, group: BankItemGroup) => {
    const groups = cloneDeep(state.gameDocument?.assets.bankItemGroups ?? []);
    if (index >= 0 && index < groups.length) {
      groups[index] = group;

      setState((prev) =>
        UpdateGameDocState(prev, {
          ...prev.gameDocument!,
          assets: {
            ...prev.gameDocument?.assets,
            bankItemGroups: groups
          }
        })
      );
    }
  };

  const onBankItemGroupChange = (id?: string) => {
    setEntity((prev) => ({
      ...prev,
      bankItemGroupId: id
    }));
  };

  const checkBankItemGroupUsage = (groupId: string): number => {
    return (
      state.gameDocument?.assets.items?.filter(
        (item) => item.bankItemGroupId === groupId
      ).length ?? 0
    );
  };

  const handleEntityEditorSubmit = (
    editorEntity: EntityEditor<ItemEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    const addTitleResource = AddResourceAsync(
      state.gameDocument!,
      'Title',
      '',
      'text',
      resourceEntity.find(
        (item) => item.entity.id === editorEntity.entity.titleResId!
      )?.entity.value!,
      titleGuid
    );
    const addSummaryResource = AddResourceAsync(
      state.gameDocument!,
      'Summary',
      '',
      'text',
      resourceEntity.find(
        (item) => item.entity.id === editorEntity.entity.summaryResId!
      )?.entity.value!,
      summaryGuid
    );

    const imageResource = resourceEntity.find(
      (item) => item.entity.id === editorEntity.entity.imageResId!
    )?.entity;
    const addImageResource = AddResourceAsync(
      state.gameDocument!,
      imageResource?.name || imageGuid,
      imageResource?.description ?? '',
      'image',
      imageResource?.value ?? '',
      imageGuid,
      imageResource?.size ?? 0,
      imageResource?.category
    );

    Promise.all([addTitleResource, addSummaryResource, addImageResource]).then(
      () => {
        const {
          entity: { name, description }
        } = editorEntity;
        AddItemAsync(
          state.gameDocument!,
          name,
          description,
          titleGuid,
          summaryGuid,
          imageGuid,
          editorEntity.entity.hideInGame,
          editorEntity.entity.maxStock,
          editorEntity.entity.useBank,
          editorEntity.entity.bankItemGroupId
        ).then((updatedGameDocument) => {
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          navigate(-1);
        });
      }
    );
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/inventory/*', text: 'Inventory' },
      { path: '/designer/:gameId/assets/inventory/:itemId/*', text: '' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar title={'New Inventory'} showDefaultContent={false}>
        <div className={'d-flex ml-2 gap-2 pl-3'}>
          <Button
            themeColor={'secondary'}
            onClick={() => {
              navigate(-1);
            }}>
            Back
          </Button>
          <Button
            onClick={() => {
              if (isInputValid()) {
                const entityResources = resources.map((item, index) => ({
                  isNew: true,
                  entity: item
                })) as EntityEditor<ResourceEntity>[];
                handleEntityEditorSubmit(
                  { isNew: true, entity },
                  entityResources
                );
              }
            }}
            themeColor={'primary'}
            fillMode={'solid'}>
            Create
          </Button>
        </div>
      </Toolbar>

      <ItemEditor
        editorMode={'full'}
        entity={entity}
        resources={resources}
        handleInputChange={handleInputChange}
        handleTextAreaChange={handleTextAreaChange}
        handleImageSelected={handleImageSelected}
        requiredFields={requiredFields}
        handleHideItemChange={handleHideItemChange}
        bankItemGroups={state.gameDocument?.assets.bankItemGroups}
        onRemoveBankItemGroup={onRemoveBankItemGroup}
        onAddBankItemGroup={onAddBankItemGroup}
        onUpdateBankItemGroup={onUpdateBankItemGroup}
        onBankItemGroupChange={onBankItemGroupChange}
        checkBankItemGroupUsage={checkBankItemGroupUsage}>
        <div className={'mt-1'}>
          <Typography.p fontSize={'xsmall'}>
            Notes: Please use images with a 1:1 aspect ratio to achieve a better
            experience on the Player Screen.
          </Typography.p>
        </div>
      </ItemEditor>
    </>
  );
};
